import React, { useState, useEffect, useRef } from "react";
import { Input, Button, Modal, Row, message } from "antd";
import { controller } from "../controller";
import { PopupMessage } from "./PopupMessage";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SignUp = (props) => {
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    password1: "",
    password2: "",
    email: "",
    token: "",
  });

  const [openModal, setOpenModal] = useState(false);
  // The invite already knows the email it was sent to; the lookup below fills
  // it in. `ok` picks where the hint renders (under the email / under the code).
  const [inviteHint, setInviteHint] = useState(null); // { ok: bool, text: string } | null
  const timerRef = useRef(null);
  const prefilledEmailRef = useRef("");

  // Registration is invite-gated. Prefill the code from a ?token= link (the
  // emailed invite); it can also be typed/pasted manually.
  useEffect(() => {
    const fromSearch = new URLSearchParams(window.location.search).get("token");
    const hashQuery = window.location.hash.includes("?") ? window.location.hash.split("?")[1] : "";
    const fromHash = hashQuery ? new URLSearchParams(hashQuery).get("token") : null;
    const token = fromSearch || fromHash;
    if (token) setData((d) => ({ ...d, token }));
  }, []);

  // Look the code up (debounced) and prefill the email the invite was sent to.
  // One effect covers both ways in: the ?token= link above writes into the same
  // state as typing or pasting the code. The email stays EDITABLE — this is a
  // convenience, not a check (sign-up itself doesn't enforce the match) — and an
  // email the user typed themselves is never overwritten; only our own prefill
  // is withdrawn when the code stops resolving.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const token = (data.token || "").trim();

    const withdrawPrefill = () => {
      setData((d) => (d.email && d.email === prefilledEmailRef.current ? { ...d, email: "" } : d));
      prefilledEmailRef.current = "";
    };

    if (!UUID_RE.test(token)) {
      setInviteHint(null);
      withdrawPrefill();
      return undefined;
    }

    let cancelled = false;
    timerRef.current = setTimeout(async () => {
      try {
        const res = await controller.lookupInvite(token);
        if (cancelled) return;
        if (res && res.status < 250 && res.email) {
          const found = res.email;
          setData((d) => (d.email && d.email !== prefilledEmailRef.current ? d : { ...d, email: found }));
          prefilledEmailRef.current = found;
          setInviteHint({ ok: true, text: "Email taken from your invite — you can change it." });
        } else {
          withdrawPrefill();
          setInviteHint({ ok: false, text: "We couldn't find an open invite for this code." });
        }
      } catch (e) {
        if (!cancelled) setInviteHint(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.token]);

  const handleChange = (e) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };

  const handleCreate = async () => {
    if (!data.token || !data.token.trim()) {
      message.error("An invite code is required to register.");
      return;
    }
    setLoading(true);
    const response = await controller.SignUp(data);
    if (response.status < 250) {
      setOpenModal(true);
      message.success("Register was successful");
    } else {
      message.error(
        response.detail
          ? response.detail
          : response.email
          ? response.email
          : response.password1
          ? response.password1
          : response.password2
          ? response.password2
          : "Error"
      );
    }
    setLoading(false);
  };

  const handleDone = () => {
    props.goToLoginPage();
  };

  const handleGoToLoginPage = () => {
    props.goToLoginPage();
  };

  return (
    <>
      <div>
        <p className="login-title">Create an account</p>
        <p className="login-subtitle" onClick={handleGoToLoginPage}>
          I have an account
        </p>
      </div>

      <div className="mt8p">
        <div className="input-lable">Invite code</div>
        <Input placeholder="Enter your invite code" onChange={handleChange} name="token" value={data.token} style={{ height: 39 }} />
        {inviteHint && !inviteHint.ok ? (
          <div style={{ fontSize: 12, color: "#cf1322", marginTop: 6 }}>{inviteHint.text}</div>
        ) : null}

        <div className="input-lable mt20">Email</div>
        <Input placeholder="Enter Email" onChange={handleChange} name="email" value={data.email} style={{ height: 39 }} />
        {inviteHint && inviteHint.ok ? (
          <div style={{ fontSize: 12, color: "#6f6c80", marginTop: 6 }}>{inviteHint.text}</div>
        ) : null}

        <div className="input-lable mt20">Password</div>
        <Input type="password" placeholder="Enter Password" onChange={handleChange} name="password1" value={data.password1} style={{ height: 39 }} />

        <div className="input-lable mt20">Confirm Password</div>
        <Input
          type="password"
          placeholder="Enter Password Again"
          onChange={handleChange}
          name="password2"
          value={data.password2}
          style={{ height: 39 }}
        />
      </div>

      <div className="mt10p">
        <Button loading={loading} className="login-button" onClick={handleCreate}>
          Create Account
        </Button>
        {/* <div>
                    <p className="forgotpass-text">Forget Password?</p>
                </div> */}
      </div>

      <Modal
        title=""
        open={openModal}
        footer={null}
        onCancel={() => {
          setOpenModal(false);
        }}
        centered={true}
      >
        <div>
          <p className="modal-titr">Verify your Email</p>
        </div>
        <div>
          <p className="modal-sub-titr">You need to verify your Email address to login.</p>
        </div>

        <Button className="done-button" loading={loading} onClick={handleDone}>
          {!loading ? "Done" : "Done..."}
        </Button>

        {/* <Row justify={"space-between"} className="mt10">
                    <p className="recive-sms-title">Did not receive the code? </p>
                    <p className="recive-sms-text">Resend code</p>
                </Row> */}
      </Modal>
    </>
  );
};

export default SignUp;
